<script lang="ts">
	/**
	 * Flowbite's `BreadcrumbItem`, forked so each crumb routes in-app.
	 *
	 * Every page in the app opens with the same two blocks — a breadcrumb trail,
	 * then the `PageHeader`. The trail is what tells the user where they are in
	 * the hierarchy before the title tells them what they are looking at, so keep
	 * the order and don't skip it on nested pages:
	 *
	 * ```svelte
	 * <Breadcrumb>
	 *   <RoutedBreadcrumbItem href="/" home>Home</RoutedBreadcrumbItem>
	 *   <RoutedBreadcrumbItem href="/Settings">Settings</RoutedBreadcrumbItem>
	 *   <RoutedBreadcrumbItem>Account</RoutedBreadcrumbItem>
	 * </Breadcrumb>
	 *
	 * <PageHeader title="Account" description="…" />
	 * ```
	 *
	 * Conventions baked in above: the first crumb is `home` (house icon, no
	 * chevron), intermediate crumbs carry an `href`, and the **last crumb has no
	 * `href`** — it renders as muted text, because a link to the page you are
	 * already on is a dead end.
	 *
	 * The fork exists only to swap the anchor's full page load for a router push;
	 * the class strings are flowbite-svelte 0.44's verbatim.
	 */

	import { twMerge } from 'tailwind-merge';
	import { onEventPushRoute } from '../svelte-spa-router';

	/** Renders the house icon and drops the leading chevron. Use on the first crumb only. */
	export let home: boolean = false;
	/** Destination path. Leave unset on the current page's own crumb so it renders as plain text. */
	export let href: string | undefined = undefined;

	/** Styling for a linked crumb. */
	export let linkClass: string = 'ml-1 text-sm font-medium text-gray-700 hover:text-gray-900 md:ml-2 dark:text-gray-400 dark:hover:text-white';
	/** Styling for the unlinked (current page) crumb — deliberately dimmer than a link. */
	export let spanClass: string = 'ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400';
	/** Styling for the `home` crumb. */
	export let homeClass: string = 'inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white';
</script>

<li class={twMerge('inline-flex items-center', $$props.class)} {...$$restProps}>
	{#if home}
		<a on:click={(event) => onEventPushRoute(event, href ?? '/')} class={twMerge(homeClass, $$props.classHome)} {href}>
			{#if $$slots.icon}
				<slot name="icon" />
			{:else}
				<svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
				</svg>
			{/if}
			<slot />
		</a>
	{:else}
		<!-- The chevron belongs to the crumb that follows it, which is why `home` skips it. -->
		{#if $$slots.icon}
			<slot name="icon" />
		{:else}
			<svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
				<path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
			</svg>
		{/if}

		{#if href}
			<a on:click={(event) => onEventPushRoute(event, href)} class={twMerge(linkClass, $$props.classLink)} {href}>
				<slot />
			</a>
		{:else}
			<span class={twMerge(spanClass, $$props.classSpan)}>
				<slot />
			</span>
		{/if}
	{/if}
</li>
